import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { Loader2, Maximize2, Pause, Play, Volume2, VolumeX } from "lucide-react";
import { cn } from "@/utils/helpers";

/*
 * Our video player (style B). The browser's own player starts as a squashed
 * strip until it knows the video's size, then jumps to it. This one holds a
 * frame of the expected shape with the loading surface pulsing in it, then
 * grows to the video's own shape once its size is known and fades the first
 * frame in, so loading reads as one smooth step. A tall video in a wide frame
 * stops at the height cap and sits centred (contain).
 *
 * The controls are ours too: a centre play while paused, and a bar (play,
 * time, seek, sound, full screen) that shows while paused, hovered or focused.
 * Space or K plays and pauses, the arrows seek five seconds.
 */

type Props = {
  src: string;
  poster?: string | null;
  /** The shape to hold until the video says its own (width / height). */
  ratio?: number;
  /** The tallest the frame grows, any CSS length. */
  maxHeight?: string;
  /** What the video is, for people who cannot see it. */
  label?: string;
  className?: string;
};

const SEEK_STEP_S = 5;

function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${rest}` : `${m}:${rest}`;
}

export function KitVideoPlayer({ src, poster, ratio = 16 / 9, maxHeight = "55dvh", label = "Video", className }: Props) {
  const frameRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [width, setWidth] = useState(0);
  const [aspect, setAspect] = useState(ratio);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  // A new source starts over: its own shape, its own load.
  const [shownSrc, setShownSrc] = useState(src);
  if (src !== shownSrc) {
    setShownSrc(src);
    setPhase("loading");
    setAspect(ratio);
    setPlaying(false);
    setTime(0);
    setDuration(0);
  }

  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // A video already in the cache can be ready before React listens.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.videoWidth && video.videoHeight) setAspect(video.videoWidth / video.videoHeight);
    if (video.readyState >= 2) setPhase("ready");
  }, [shownSrc]);

  const toggle = () => {
    const video = videoRef.current;
    if (!video || phase === "error") return;
    if (video.paused) void video.play().catch(() => {});
    else video.pause();
  };

  const seekTo = (seconds: number) => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration)) return;
    video.currentTime = Math.min(Math.max(0, seconds), video.duration);
    setTime(video.currentTime);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target instanceof HTMLInputElement) return;
    if (event.key === " " || event.key === "k") {
      event.preventDefault();
      toggle();
    } else if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      seekTo(time + (event.key === "ArrowRight" ? SEEK_STEP_S : -SEEK_STEP_S));
    }
  };

  const fullScreen = () => {
    const frame = frameRef.current;
    if (!frame) return;
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    else void frame.requestFullscreen?.().catch(() => {});
  };

  const ready = phase === "ready";
  const height = width > 0 ? width / aspect : undefined;
  const progress = duration > 0 ? (time / duration) * 100 : 0;

  return (
    <div
      ref={frameRef}
      role="group"
      aria-label={label}
      tabIndex={0}
      onKeyDown={onKeyDown}
      data-phase={phase}
      data-playing={playing || undefined}
      className={cn(
        "group/video relative w-full overflow-hidden rounded-card bg-co-surface-solid outline-none",
        "transition-[height] duration-[420ms] ease-[var(--ease-smooth)] motion-reduce:transition-none",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
        phase === "loading" && "animate-[co-skeleton-fade_1.8s_ease-in-out_infinite]",
        className,
      )}
      style={{ height, maxHeight }}
    >
      {poster && !ready ? (
        <img src={poster} alt="" aria-hidden className="absolute inset-0 size-full object-contain opacity-60" />
      ) : null}
      <video
        ref={videoRef}
        key={shownSrc}
        src={shownSrc}
        poster={poster ?? undefined}
        playsInline
        preload="metadata"
        muted={muted}
        onClick={toggle}
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          if (v.videoWidth && v.videoHeight) setAspect(v.videoWidth / v.videoHeight);
          setDuration(Number.isFinite(v.duration) ? v.duration : 0);
        }}
        onLoadedData={() => setPhase("ready")}
        onCanPlay={() => setPhase((p) => (p === "loading" ? "ready" : p))}
        onError={() => setPhase("error")}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onWaiting={() => setBuffering(true)}
        onPlaying={() => setBuffering(false)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onDurationChange={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d)) setDuration(d);
        }}
        onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
        className={cn(
          "absolute inset-0 size-full cursor-pointer object-contain transition-opacity duration-300 ease-out",
          ready ? "opacity-100" : "opacity-0",
        )}
      />

      {phase === "error" ? (
        <p className="absolute inset-0 grid place-items-center px-6 text-center text-[13px] text-co-muted">
          This video couldn't be played.
        </p>
      ) : null}

      {/* Paused: one big play in the middle. Buffering mid-play: a small spinner. */}
      {ready && !playing ? (
        <button
          type="button"
          onClick={toggle}
          aria-label="Play"
          className="co-glass-clear absolute top-1/2 left-1/2 grid size-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full text-white transition-transform duration-200 hover:scale-105"
        >
          <Play aria-hidden className="ml-0.5 size-6" fill="currentColor" strokeWidth={0} />
        </button>
      ) : null}
      {ready && playing && buffering ? (
        <Loader2 aria-hidden className="pointer-events-none absolute top-1/2 left-1/2 size-6 -translate-x-1/2 -translate-y-1/2 animate-spin text-white/80" />
      ) : null}

      {ready ? (
        <div
          className={cn(
            "absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/70 to-transparent px-3 pt-8 pb-2.5 text-white",
            "transition-opacity duration-200",
            playing ? "opacity-0 group-hover/video:opacity-100 group-focus-within/video:opacity-100" : "opacity-100",
          )}
        >
          <button
            type="button"
            onClick={toggle}
            aria-label={playing ? "Pause" : "Play"}
            className="grid size-8 shrink-0 place-items-center rounded-full hover:bg-white/15"
          >
            {playing ? (
              <Pause aria-hidden className="size-4" fill="currentColor" strokeWidth={0} />
            ) : (
              <Play aria-hidden className="ml-px size-4" fill="currentColor" strokeWidth={0} />
            )}
          </button>
          <span className="shrink-0 font-mono text-[10.5px] tracking-[0.06em] tabular-nums">
            {clock(time)} / {clock(duration)}
          </span>
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={Math.min(time, duration || 0)}
            onChange={(e) => seekTo(Number(e.target.value))}
            aria-label="Seek"
            aria-valuetext={`${clock(time)} of ${clock(duration)}`}
            className="kit-video-seek h-1 min-w-0 flex-1 cursor-pointer appearance-none rounded-full"
            style={{
              background: `linear-gradient(to right, var(--co-accent) ${progress}%, rgb(255 255 255 / 0.3) ${progress}%)`,
            }}
          />
          <button
            type="button"
            onClick={() => {
              const video = videoRef.current;
              if (video) video.muted = !video.muted;
            }}
            aria-label={muted ? "Turn the sound on" : "Mute"}
            className="grid size-8 shrink-0 place-items-center rounded-full hover:bg-white/15"
          >
            {muted ? <VolumeX aria-hidden className="size-4" /> : <Volume2 aria-hidden className="size-4" />}
          </button>
          <button
            type="button"
            onClick={fullScreen}
            aria-label="Full screen"
            className="grid size-8 shrink-0 place-items-center rounded-full hover:bg-white/15"
          >
            <Maximize2 aria-hidden className="size-4" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
